import type { TreeDataNode } from 'antd';
import { ID_SEPARATOR } from 'constants/appConstants';
import {
  FARM_KEY_PREFIX,
  INPUT_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  COPY_TOOL_SOURCE_ONLY_PADDOCK_DISABLED_TOOLTIP,
  COPY_TOOL_SOURCE_ROW_DISABLED_TOOLTIP,
} from 'constants/uiConstants';
import { IFarm } from 'interfaces/IFarm';
import { IInput } from 'interfaces/IInput';
import {
  createFarmNode,
  createInputNode,
  createPaddockNode,
} from 'pages/FarmsPage/Sidebar/FarmTreeViewNodes';
import {
  filterInputsByYear,
  sortFarmsByName,
  sortPaddocksByName,
} from 'utils/FarmUtils';
import MergeClassNames from 'utils/MergeClassNames';
import { toHectaresOrNull } from 'utils/unitConversion';

export type CopyTreeNodeInfo =
  | { kind: 'farm'; farmId: string }
  | {
      kind: 'paddock';
      farmId: string;
      paddockId: string;
      /** Area a new document here would get (its Zone's area), in ha; null when unknown. */
      areaHa: number | null;
      /** Keys of the paddock's tickable plan nodes for the tree's year. */
      inputKeys: string[];
      /** No plans at all that year, so ticking it creates a new document. */
      isEmpty: boolean;
    }
  | {
      kind: 'input';
      farmId: string;
      paddockId: string;
      input: IInput;
      /** The plan's zone area in ha; null when missing, 0 or not convertible. */
      zoneAreaHa: number | null;
    };

/** What every node key in a copy tree stands for, so selection never parses keys. */
export type CopyTreeIndex = Map<string, CopyTreeNodeInfo>;

export interface CopyTreeData {
  treeData: TreeDataNode[];
  index: CopyTreeIndex;
  /** Built for Add as new: only paddocks are tickable. */
  paddocksOnly: boolean;
}

export interface BuildCopyTreeDataParams {
  farms: IFarm[];
  inputsByPaddock: Map<string, IInput[]>;
  year: number;
  /** Paddocks get a select-all tick, and empty paddocks can be ticked. */
  allowMultiple: boolean;
  /** Only paddocks are tickable; their plans are listed for context (Add as new). */
  paddocksOnly?: boolean;
  /** Each Zone's area in ha, by zone id; a new plan takes its paddock's Zone area. */
  zoneAreaHaById: Map<string, number | null>;
}

/** A tree row; a greyed-out tick carries the reason, shown on hover. */
export interface CopyTreeNode extends TreeDataNode {
  disabledReason?: string;
}

/** Greys out a row's tick, with a not-allowed cursor and the reason shown on hover. */
export const disableCopyTreeNode = (
  node: TreeDataNode,
  reason: string
): CopyTreeNode => ({
  ...node,
  className: MergeClassNames(node.className, 'copy-tree-row-disabled'),
  disableCheckbox: true,
  disabledReason: reason,
});

export const getCopyPaddockKey = (paddockId: string) =>
  `${PADDOCK_KEY_PREFIX}${paddockId}`;

// Paddock-scoped, since a plan linked to two paddocks appears under both.
export const getCopyInputKey = (paddockId: string, inputId: string) =>
  `${INPUT_KEY_PREFIX}${paddockId}${ID_SEPARATOR}${inputId}`;

/**
 * Farm → paddock → plan tree for one side of the copy tool, limited to the
 * given year (undated plans show in every year, as in the sidebar tree).
 *
 * Plans are tickable unless `paddocksOnly`; paddocks and farms only when `allowMultiple` is on.
 */
export const buildCopyTreeData = ({
  farms,
  inputsByPaddock,
  year,
  allowMultiple,
  paddocksOnly = false,
  zoneAreaHaById,
}: BuildCopyTreeDataParams): CopyTreeData => {
  const index: CopyTreeIndex = new Map();

  const treeData = sortFarmsByName(farms)
    .filter((farm) => farm.paddocks.length > 0)
    .map((farm) => {
      const farmKey = `${FARM_KEY_PREFIX}${farm.id}`;
      index.set(farmKey, { kind: 'farm', farmId: farm.id });

      const paddockNodes = sortPaddocksByName(farm.paddocks).map((paddock) => {
        const paddockKey = getCopyPaddockKey(paddock.paddockId);
        const inputs = filterInputsByYear(
          inputsByPaddock.get(paddock.paddockId) ?? [],
          year
        );

        const inputNodes: TreeDataNode[] = inputs.map((input) => {
          const inputKey = getCopyInputKey(paddock.paddockId, input.id);
          index.set(inputKey, {
            kind: 'input',
            farmId: farm.id,
            paddockId: paddock.paddockId,
            input,
            zoneAreaHa: toHectaresOrNull(
              input.zone?.zoneArea,
              input.zone?.areaUnit
            ),
          });
          return {
            ...createInputNode(input),
            key: inputKey,
            checkable: !paddocksOnly,
          };
        });

        const inputKeys = inputNodes.map((node) => String(node.key));
        const isEmpty = inputNodes.length === 0;
        index.set(paddockKey, {
          kind: 'paddock',
          farmId: farm.id,
          paddockId: paddock.paddockId,
          areaHa: zoneAreaHaById.get(paddock.zoneId) ?? null,
          inputKeys,
          isEmpty,
        });

        return {
          ...createPaddockNode(paddock, []),
          key: paddockKey,
          children: inputNodes,
          isLeaf: isEmpty,
          checkable: allowMultiple,
        };
      });

      return {
        ...createFarmNode(farm, paddockNodes),
        key: farmKey,
        // Ticks every paddock shown under it.
        checkable: allowMultiple,
      };
    });

  return { treeData, index, paddocksOnly };
};

// Built once per tree, so large organisations don't rescan the index on every tick.
const inputKeysByIdCache = new WeakMap<CopyTreeIndex, Map<string, string[]>>();

/** Every key a plan has in the tree: one per paddock it links to. */
export const getInputKeysById = (
  index: CopyTreeIndex
): Map<string, string[]> => {
  const cached = inputKeysByIdCache.get(index);
  if (cached) return cached;
  const byId = new Map<string, string[]>();
  index.forEach((info, key) => {
    if (info.kind !== 'input') return;
    const keys = byId.get(info.input.id);
    if (keys) keys.push(key);
    else byId.set(info.input.id, [key]);
  });
  inputKeysByIdCache.set(index, byId);
  return byId;
};

/**
 * Shows the Source document untickable so it can't be copied onto itself, and
 * leaves it out of the index so selection and copy targets never include it.
 * A light pass over a built tree: only the Source's paddocks and farms are
 * copied, so changing the Source doesn't rebuild the whole Destination tree.
 * Not applied in Add as new, whose targets are paddocks: a new document in
 * the Source's paddock isn't a self-copy.
 */
export const excludeInputFromCopyTree = (
  tree: CopyTreeData,
  inputId: string | null
): CopyTreeData => {
  const baseById = getInputKeysById(tree.index);
  const excludedKeys = new Set(inputId ? (baseById.get(inputId) ?? []) : []);
  if (excludedKeys.size === 0) return tree;

  const index: CopyTreeIndex = new Map(tree.index);
  const affectedPaddockKeys = new Set<string>();
  excludedKeys.forEach((key) => {
    const info = index.get(key);
    index.delete(key);
    if (info?.kind !== 'input') return;
    const paddockKey = getCopyPaddockKey(info.paddockId);
    const paddock = index.get(paddockKey);
    if (paddock?.kind !== 'paddock') return;
    affectedPaddockKeys.add(paddockKey);
    index.set(paddockKey, {
      ...paddock,
      inputKeys: paddock.inputKeys.filter((inputKey) => inputKey !== key),
    });
  });

  const byId = new Map(baseById);
  if (inputId) byId.delete(inputId);
  inputKeysByIdCache.set(index, byId);

  const treeData = tree.treeData.map((farm) => {
    const paddocks = farm.children ?? [];
    if (
      !paddocks.some((paddock) => affectedPaddockKeys.has(String(paddock.key)))
    ) {
      return farm;
    }
    return {
      ...farm,
      children: paddocks.map((paddock) => {
        const paddockKey = String(paddock.key);
        if (!affectedPaddockKeys.has(paddockKey)) return paddock;
        const info = index.get(paddockKey);
        const paddockNode = {
          ...paddock,
          children: (paddock.children ?? []).map((node) =>
            excludedKeys.has(String(node.key))
              ? disableCopyTreeNode(node, COPY_TOOL_SOURCE_ROW_DISABLED_TOOLTIP)
              : node
          ),
        };
        // Its only plan is the Source document, so there's nothing to tick.
        return info?.kind === 'paddock' && info.inputKeys.length === 0
          ? disableCopyTreeNode(
              paddockNode,
              COPY_TOOL_SOURCE_ONLY_PADDOCK_DISABLED_TOOLTIP
            )
          : paddockNode;
      }),
    };
  });

  return { treeData, index, paddocksOnly: tree.paddocksOnly };
};
